import React, { useMemo, useEffect } from "react";

import getPasswordStrengthIndicators from "./helpers/getPasswordStrengthIndicators";
import validatePassword from "./helpers/validatePassword";

import Typography from "../Typography/Typography";
import Icon from "../Icon/Icon";

import styles from "./PasswordStrength.module.css";

interface Props {
    passwordToValidate: string;
    onChange?: (isValid: boolean) => void;
}

const PasswordStrength = ({ passwordToValidate, onChange }: Props) => {
    const validation = useMemo(() => {
        const result = { ...validatePassword(passwordToValidate) };
        const indicators = getPasswordStrengthIndicators(result.score);

        return { ...result, ...indicators };
    }, [passwordToValidate]);

    useEffect(() => {
        if (!onChange) return;
        onChange(validation.score === 100);
    }, [validation.score, onChange]);

    const validLength = validation.validRules.includes("validLength");
    const containsUppercase = validation.validRules.includes("containsUppercase");
    const containsNumber = validation.validRules.includes("containsNumber");

    return (
        <div className="space-y-0.5">
            <div className="flex gap-0.5 items-center">
                <Typography as="div" variant="meta" color="midnight" className="flex-shrink-0 flex-grow-0 w-[4rem]">
                    {validation.label}
                </Typography>
                <div className="flex-grow h-[2px]">
                    <div style={{ width: `${validation.score}%` }} className={`${styles.bar} ${validation.color}`} />
                </div>
            </div>
            <div className="space-y-0.25">
                <div className="flex flex-col gap-0.25">
                    <div className="flex justify-between items-center">
                        <Typography as="div" variant="meta" color="pine">
                            At least 6 characters
                        </Typography>
                        <Icon icon={validLength ? "check" : "cross"} className={validLength ? "text-mint-300" : "text-plum-500"} />
                    </div>
                    <div className="flex justify-between items-center">
                        <Typography as="div" variant="meta" color="pine">
                            Includes an upper case letter
                        </Typography>
                        <Icon
                            icon={containsUppercase ? "check" : "cross"}
                            className={containsUppercase ? "text-mint-300" : "text-plum-500"}
                        />
                    </div>
                    <div className="flex justify-between items-center">
                        <Typography as="div" variant="meta" color="pine">
                            Includes a number
                        </Typography>
                        <Icon icon={containsNumber ? "check" : "cross"} className={containsNumber ? "text-mint-300" : "text-plum-500"} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PasswordStrength;
